/**
 * Design system component: DueDateBadge
 *
 * Amber chip that surfaces overdue or due-today status on loan items.
 * Returns null when the loan has no due date or the due date is in the future
 * (not today). Both states use identical amber styling — the text does the
 * communication work ("Due today" vs "X days overdue").
 *
 * Follows the same visual pattern as PolicyBadge: inline pill, 11px/600,
 * semantic warning colours, 6px border-radius.
 */

import { T } from '@/lib/tokens';

interface DueDateBadgeProps {
  /** ISO date string for the loan's due_at, or null for open-ended loans. */
  dueAt: string | null;
  className?: string;
}

/**
 * Returns the number of calendar days between two dates (ignoring time).
 * Positive = overdue (due date in the past), negative = days remaining.
 */
function getDaysDiff(dueAt: string): number {
  const now = new Date();
  const due = new Date(dueAt);
  // Compare calendar dates, not timestamps
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDate = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  return Math.floor((nowDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
}

export function DueDateBadge({ dueAt, className }: DueDateBadgeProps) {
  if (!dueAt) return null;

  const daysDiff = getDaysDiff(dueAt);

  // Future due date (not today) — no badge
  if (daysDiff < 0) return null;

  const label = daysDiff === 0
    ? 'Due today'
    : daysDiff === 1
      ? '1 day overdue'
      : `${daysDiff} days overdue`;

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 6,
        background: T.warningSoft,
        color: T.warning,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.03em',
        border: `1px solid ${T.warningBorder}`,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

/**
 * Utility: checks if a due date is overdue or due today.
 * Useful for sort logic without rendering the badge.
 */
export function getDueStatus(dueAt: string | null): 'overdue' | 'due-today' | 'ok' | null {
  if (!dueAt) return null;
  const daysDiff = getDaysDiff(dueAt);
  if (daysDiff > 0) return 'overdue';
  if (daysDiff === 0) return 'due-today';
  return 'ok';
}

/**
 * Sort comparator: overdue items first (most overdue first), then due-today,
 * then everything else. Items without due dates sort last.
 */
export function compareDueUrgency(a: string | null, b: string | null): number {
  const aStatus = getDueStatus(a);
  const bStatus = getDueStatus(b);

  const priority = (s: string | null) => {
    if (s === 'overdue') return 0;
    if (s === 'due-today') return 1;
    return 2;
  };

  const diff = priority(aStatus) - priority(bStatus);
  if (diff !== 0) return diff;

  // Both overdue — most overdue (earliest due date) first
  if (aStatus === 'overdue' && bStatus === 'overdue' && a && b) {
    return new Date(a).getTime() - new Date(b).getTime();
  }

  return 0;
}
